/**
 * iter-338x — AI Chat BOQ search results list, scalable to 100s+ of hits.
 *
 * UX principles:
 *  - No visual grouping — flat ordered list keeps it fast to scan even at 1000+ rows.
 *  - A single filter chip row at the top: "All (n)" / "Title (n)" / "Description (n)" /
 *    "Category (n)" lets the user narrow down in one click.
 *  - Only the top N (default 5) cards render; a "Show M more" button expands the rest
 *    in chunks so the DOM never grows unbounded.
 *  - Rows are pre-sorted by (match_field priority: title > description > category, then
 *    match_score DESC) so the most useful hit is always on top.
 *  - Each card carries a tiny colour-coded dot on the left indicating the match field
 *    instead of a full sub-section header — preserves the "know where it hit" signal
 *    without the visual clutter.
 */
import { useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';

const INITIAL_LIMIT = 5;
const CHUNK = 5;

const FIELD_META = {
  title:       { dot: 'bg-blue-600',  label: 'Title',       priority: 0 },
  description: { dot: 'bg-stone-500', label: 'Description', priority: 1 },
  category:    { dot: 'bg-stone-400', label: 'Category',    priority: 2 },
};

function fmtINR(n) {
  return n != null ? `Rs ${Math.round(+n || 0).toLocaleString('en-IN')}` : '—';
}

export default function BOQResultsList({ rows, messageIndex }) {
  const [activeFilter, setActiveFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(INITIAL_LIMIT);

  const counts = useMemo(() => {
    const c = { all: rows.length, title: 0, description: 0, category: 0 };
    rows.forEach((r) => {
      const mf = FIELD_META[r.match_field] ? r.match_field : 'category';
      c[mf] += 1;
    });
    return c;
  }, [rows]);

  const orderedRows = useMemo(() => {
    // Server already sorts by score. Add a stable secondary sort by
    // match_field priority so title matches always float above description
    // matches with the same score — critical when the count is high.
    const withPri = rows.map((r) => ({
      r,
      p: FIELD_META[r.match_field]?.priority ?? 2,
      s: -(r.match_score || 0),
    }));
    withPri.sort((a, b) => (a.p - b.p) || (a.s - b.s));
    return withPri.map((x) => x.r);
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (activeFilter === 'all') return orderedRows;
    return orderedRows.filter((r) => (r.match_field || 'category') === activeFilter);
  }, [orderedRows, activeFilter]);

  const visible = filteredRows.slice(0, visibleCount);
  const hidden = Math.max(0, filteredRows.length - visibleCount);

  if (rows.length === 0) return null;

  const CHIPS = [
    { key: 'all', label: 'All' },
    { key: 'title', label: 'Title' },
    { key: 'description', label: 'Description' },
    { key: 'category', label: 'Category' },
  ];

  return (
    <div className="mt-2 pt-2 border-t border-stone-200/70" data-testid={`ai-chat-cards-${messageIndex}`}>
      {/* Filter chip row */}
      <div className="flex flex-wrap gap-1 mb-1.5" data-testid={`ai-chat-filter-chips-${messageIndex}`}>
        {CHIPS.map((c) => {
          const n = counts[c.key] || 0;
          if (c.key !== 'all' && n === 0) return null;
          const active = activeFilter === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => { setActiveFilter(c.key); setVisibleCount(INITIAL_LIMIT); }}
              className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border transition-all ${
                active
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'
              }`}
              data-testid={`ai-chat-filter-chip-${messageIndex}-${c.key}`}
            >
              {c.label} ({n})
            </button>
          );
        })}
      </div>

      {/* Flat card list */}
      <div className="space-y-1">
        {visible.map((r, j) => {
          const meta = FIELD_META[r.match_field] || FIELD_META.category;
          const focusParam = r.item_id ? `&focusItem=${encodeURIComponent(r.item_id)}` : '';
          const href = `/boq-worksheets/${r.worksheet_id}/edit?openProfitability=0${focusParam}`;
          return (
            <a
              key={j}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="ai-chat-card block"
              data-testid={`ai-chat-card-${messageIndex}-${j}`}
              title={`Match: ${meta.label} — open this BOQ in a new tab`}
            >
              <div className="flex items-center gap-1.5">
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${meta.dot}`}
                  title={meta.label}
                  data-testid={`ai-chat-card-dot-${messageIndex}-${j}`}
                />
                <div className="font-semibold text-stone-800 truncate text-[11.5px] flex-1">
                  {r.task_title || (r.description || '').slice(0, 60) || '—'}
                </div>
                <ExternalLink size={10} className="text-stone-500 shrink-0" />
              </div>
              <div className="text-stone-500 text-[10px] mt-0.5 truncate pl-3">
                {r.project_name || '—'} · {r.worksheet_name || r.worksheet_number}
                {r.qty ? ` · ${r.qty} ${r.unit || ''}` : ''}
                {r.vendor_rate != null ? ` · ${fmtINR(r.vendor_rate)}` : ''}
              </div>
            </a>
          );
        })}
      </div>

      {/* Show-more / collapse */}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setVisibleCount((c) => c + CHUNK)}
          className="mt-1.5 w-full text-center text-[10px] font-medium text-blue-600 hover:text-blue-700 py-1 rounded-md hover:bg-blue-50 transition-colors"
          data-testid={`ai-chat-show-more-${messageIndex}`}
        >
          Show {Math.min(CHUNK, hidden)} more
          {hidden > CHUNK ? ` (${hidden} total)` : ''}
        </button>
      )}
      {visibleCount > INITIAL_LIMIT && hidden === 0 && (
        <button
          type="button"
          onClick={() => setVisibleCount(INITIAL_LIMIT)}
          className="mt-1.5 w-full text-center text-[10px] font-medium text-stone-500 hover:text-stone-700 py-1 rounded-md hover:bg-stone-50 transition-colors"
          data-testid={`ai-chat-collapse-${messageIndex}`}
        >
          Collapse
        </button>
      )}
    </div>
  );
}
